import React, { useState } from 'react'
import { Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { selectDatasetSources, setDatasetSources } from '../store/slices/datasetSlice'
import { useAppDispatch, useAppSelector, useAnnotationStats } from '../store/hooks'
import PillsDataset from './PillsDataset'
import SelectDatasetModal from './modals/SelectDatasetModal'
import { useGetTagsQuery } from '../services/tagApi'
import { useGetProjectQuery } from '../services/projectsApi'
import { FaBoxes, FaTags, FaImages } from 'react-icons/fa'
import { selectSelectedProjectId } from '../store/slices/projectSlice'

interface SelectedDatasetsCardProps {
  singleSelect?: boolean
  compactView?: boolean
  readOnly?: boolean
  showProvidedSources?: boolean
  providedSources?: string[]
  onSourcesChange?: (sources: string[]) => void
  title?: string
}

const SelectedDatasetsCard: React.FC<SelectedDatasetsCardProps> = ({
  singleSelect = false,
  compactView = false,
  readOnly = false,
  showProvidedSources = false,
  providedSources,
  onSourcesChange,
  title,
}) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const appDatasetSources = useAppSelector(selectDatasetSources)

  // Use provided sources if in readOnly mode, otherwise use from Redux
  const datasetSources = showProvidedSources ? providedSources || [] : appDatasetSources

  const [showModal, setShowModal] = useState(false)

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    datasetSources
  )

  const totalAnnotations = annoStats?.statistics.total_annotations

  const handleAcceptSelections = (sources: string[]) => {
    if (readOnly && onSourcesChange) {
      onSourcesChange(sources)
    } else {
      dispatch(setDatasetSources(sources))
    }
    setShowModal(false)
  }

  const handleClose = () => setShowModal(false)

  const renderStats = () => (
    <Row className={compactView ? 'g-2' : ''}>
      <Col className="d-flex align-items-center justify-content-center">
        <OverlayTrigger
          placement="top-end"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="button-tooltip">Tags</Tooltip>}
        >
          <Card.Text className={compactView ? 'mb-0' : ''}>
            <FaTags size={16} className="text-info" style={{ marginRight: '5px' }} />
            {tags?.tags?.length || 0}
          </Card.Text>
        </OverlayTrigger>
      </Col>
      <Col className="d-flex align-items-center justify-content-center">
        <OverlayTrigger
          placement="top-end"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="button-tooltip">Images</Tooltip>}
        >
          <Card.Text className={compactView ? 'mb-0' : ''}>
            <FaImages size={16} className="text-info" style={{ marginRight: '5px' }} />
            {(annoStats?.statistics.total_unannotated_images || 0) +
              (annoStats?.statistics.total_annotated_images || 0)}
          </Card.Text>
        </OverlayTrigger>
      </Col>
      <Col className="d-flex align-items-center justify-content-center">
        <OverlayTrigger
          placement="top-end"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip id="button-tooltip">Annotations</Tooltip>}
        >
          <Card.Text className={compactView ? 'mb-0' : ''}>
            <FaBoxes size={16} className="text-info" style={{ marginRight: '5px' }} />
            {totalAnnotations}
          </Card.Text>
        </OverlayTrigger>
      </Col>
    </Row>
  )

  if (compactView) {
    return (
      <>
        <Card
          className="gallery-tools"
          style={{
            borderRadius: '8px',
            cursor: readOnly ? 'default' : 'pointer',
            padding: '0.2rem',
            height: '100%',
          }}
          onClick={() => !readOnly && setShowModal(true)}
        >
          <Card.Body
            className="p-2"
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {annoStats && (
              <div className="d-flex flex-column gap-1">
                <div className="d-flex align-items-center">
                  <OverlayTrigger
                    placement="top-end"
                    delay={{ show: 250, hide: 400 }}
                    overlay={<Tooltip id="tags-tooltip">{tags?.tags?.length || 0} Tags</Tooltip>}
                  >
                    <span className="small">
                      <FaTags size={16} className="text-info me-2" />
                      {tags?.tags?.length || 0}
                    </span>
                  </OverlayTrigger>
                </div>
                <div className="d-flex align-items-center">
                  <OverlayTrigger
                    placement="top-end"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                      <Tooltip id="images-tooltip">
                        {(annoStats?.statistics.total_unannotated_images || 0) +
                          (annoStats?.statistics.total_annotated_images || 0)}{' '}
                        Images
                      </Tooltip>
                    }
                  >
                    <span className="small">
                      <FaImages size={16} className="text-info me-2" />
                      {(annoStats?.statistics.total_unannotated_images || 0) +
                        (annoStats?.statistics.total_annotated_images || 0)}
                    </span>
                  </OverlayTrigger>
                </div>
                <div className="d-flex align-items-center">
                  <OverlayTrigger
                    placement="top-end"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                      <Tooltip id="annotations-tooltip">{totalAnnotations} Annotations</Tooltip>
                    }
                  >
                    <span className="small">
                      <FaBoxes size={16} className="text-info me-2" />
                      {totalAnnotations}
                    </span>
                  </OverlayTrigger>
                </div>
                <div className="mt-1">
                  {datasetSources && datasetSources.length > 0 ? (
                    <PillsDataset datasetNames={datasetSources} disabled={true} tiny={true} />
                  ) : (
                    <PillsDataset datasetNames={['All']} disabled={true} tiny={true} />
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
        {!readOnly && (
          <SelectDatasetModal
            canCreate={false}
            singleSelect={singleSelect}
            handleAcceptSelections={handleAcceptSelections}
            show={showModal}
            onHide={handleClose}
            selectedSources={datasetSources}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Card
        className="gallery-tools mb-2"
        style={{
          borderRadius: '10px',
          cursor: readOnly ? 'default' : 'pointer',
          height: '100%',
        }}
        onClick={() => !readOnly && setShowModal(true)}
      >
        <Card.Title className="mb-0">
          {title || `Selected Dataset${datasetSources && datasetSources.length > 1 ? 's' : ''}`}
        </Card.Title>
        <Card.Subtitle className="mb-2 text-muted">Annotation Statistics</Card.Subtitle>
        {annoStats && renderStats()}
        <Card.Body style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {datasetSources && datasetSources.length > 0 && (
            <PillsDataset
              datasetNames={datasetSources && datasetSources.length > 0 ? datasetSources : ['All']}
              disabled={true}
              tiny={false}
            />
          )}
          {!datasetSources?.length && (
            <PillsDataset datasetNames={['All']} disabled={true} tiny={false} />
          )}
        </Card.Body>
      </Card>
      {!readOnly && (
        <SelectDatasetModal
          canCreate={false}
          singleSelect={singleSelect}
          handleAcceptSelections={handleAcceptSelections}
          show={showModal}
          onHide={handleClose}
          selectedSources={datasetSources}
        />
      )}
    </>
  )
}

export default SelectedDatasetsCard
